/**
 * @module builder
 */
import type { OptionsDocument, QuoteDocument } from 'paintscout'

import type { RenderableItem } from '../items'
import { getItems } from '../items'
import type { QuoteItemView } from '../types'
import { getQuoteOptions } from '../util'
import { applyWorkOrderCardOrder, getWorkOrderCardLayouts } from './work-order-layout'
import type { WorkOrderLayoutView } from './work-order-layout'

/** Hidden work-order cards for each view (area / substrate), plus the combined count. */
export interface WorkOrderHiddenItems {
  area: RenderableItem[]
  substrate: RenderableItem[]
  total: number
}

/**
 * Build the full set of work-order cards for one view, in the view's saved card order — the same
 * item set the work-order grids render (bid + added-options + additional all share the view's layout
 * bucket), so hidden filtering and keys line up exactly.
 */
export function getWorkOrderViewItems(args: {
  quote: QuoteDocument
  options: OptionsDocument
  view: QuoteItemView
  showValues?: boolean
  lang?: string
}): RenderableItem[] {
  const { quote, options, view, showValues, lang } = args
  const quoteOptions = getQuoteOptions({ options, quote })
  const common = {
    quote,
    view,
    consumer: 'crew' as const,
    options,
    overrideOptions: { showQuantities: true },
    showLineItems: true,
    showValues,
    hideDimensions: quoteOptions.hideWorkOrderDimensions,
    lang
  }
  const items = [
    ...getItems({ ...common, section: 'bid' }),
    ...getItems({ ...common, section: 'added-options' }),
    ...getItems({ ...common, section: 'additional' })
  ]
  return applyWorkOrderCardOrder(items, quote.workOrderLayout?.[view]?.order)
}

/**
 * Collect the hidden work-order cards across both the Area and Surface views (per-card `hidden`
 * flags on `quote.workOrderLayout[view].cards`). Powers the top-bar "Hidden items (N)" count and the
 * unified "Hidden items" section.
 */
export function getWorkOrderHiddenItems(args: {
  quote: QuoteDocument
  options: OptionsDocument
  lang?: string
}): WorkOrderHiddenItems {
  const { quote, options, lang } = args
  const quoteOptions = getQuoteOptions({ options, quote })

  const collect = (view: WorkOrderLayoutView, showValues?: boolean): RenderableItem[] => {
    const cards = getWorkOrderCardLayouts(quote, view)
    if (Object.keys(cards).length === 0) {
      return []
    }
    return getWorkOrderViewItems({ quote, options, view, showValues, lang }).filter((item) => cards[item.key]?.hidden)
  }

  const area = collect('area', quoteOptions.showWorkOrderAreaValues)
  const substrate = collect('substrate', quoteOptions.showWorkOrderSubstrateValues)
  return { area, substrate, total: area.length + substrate.length }
}
